import { useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import {
  getInitials,
  getRootUrl,
  isBefore,
  isToday,
  toFormattedDate,
} from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { MiAvatar } from "../../MiAvatar/MiAvatar";
import { MiMenu } from "../../MiMenu/MiMenu";
import { IPrioritySchedulerColumnHeaderProps } from "../PrioritySchedulerBoard.types";

const MENU_TYPES = {
  NONE: "none",
  INITIAL: "initial",
  VIEW: "view",
};

export function PrioritySchedulerColumnHeader({
  user,
  date,
  eventCount = 0,
  holdCount = 0,
  incompleteCount = 0,
  dateView = false,
  actions,
  onActionClick,
}: Readonly<IPrioritySchedulerColumnHeaderProps>) {
  const { theme, classes, cx } = useStyles();
  const [menuType, setMenuType] = useState(MENU_TYPES.INITIAL);

  const onMenuOpen = () => {
    setMenuType(MENU_TYPES.INITIAL);
  };

  const onMenuClose = () => {
    setMenuType(MENU_TYPES.NONE);
  };

  const displayName =
    user?.displayName ?? `${user?.firstName} ${user?.lastName}`;
  const profilePictureRefId = user?.profilePicture?.referenceId;
  const profilePictureUrl = profilePictureRefId
    ? `${getRootUrl()}/${profilePictureRefId}`
    : null;

  const menuData = useMemo(() => {
    if (isBefore(date, new Date())) {
      return [];
    }

    return [
      ...actions.map((action) => ({
        label: action.label,
        icon: action.icon,
        color: action.iconColor?.(theme),
        action: action.actions
          ? () => setMenuType(action.type as unknown as string)
          : () => onActionClick?.(action.type, user, date),
        keepMenuOpen: Boolean(action.actions),
        hide:
          menuType !== MENU_TYPES.INITIAL ||
          (user && action.getIsActionHidden?.(user)),
      })),
      ...(
        actions.find((a) => (a.type as unknown as string) === menuType)
          ?.actions || []
      ).map((action) => ({
        label: action.label,
        icon: action.icon,
        color: action.iconColor?.(theme),
        action: () => onActionClick?.(action.type, user, date),
        hide: (user && action.getIsActionHidden?.(user)) ?? false,
      })),
    ];
  }, [actions, onActionClick, user, menuType, dateView, theme, date]);

  return (
    <Box className={classes.headerContainer}>
      {dateView ? (
        <Box
          display="flex"
          flex={1}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="HD1" noWrap textTransform="uppercase">
            {toFormattedDate(date, DATE_TIME_FORMATS.FORMAT_DDD)}
          </Typography>
          {isToday(date) ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <Typography
                className={classes.todayBadge}
                variant="BD1"
                noWrap
                color="info"
              >
                Today
              </Typography>
            </Box>
          ) : (
            <Typography variant="BD2" noWrap textTransform="uppercase">
              {toFormattedDate(date, DATE_TIME_FORMATS.FORMAT_MM_DD_YY)}
            </Typography>
          )}
        </Box>
      ) : (
        <Box className={classes.userDetails}>
          <MiAvatar
            className={classes.avatar}
            data-testid="user-initials"
            src={profilePictureUrl}
          >
            {getInitials(displayName)}
          </MiAvatar>
          <Typography variant="HD1" className={classes.titleText}>
            {displayName}
          </Typography>
        </Box>
      )}
      <Box className={classes.rightContentContainer}>
        {!!user && (
          <Box className={classes.badgesContainer}>
            {eventCount > 0 && (
              <Box className={cx(classes.countBadge)}>
                <Typography variant="BD1">{eventCount}</Typography>
              </Box>
            )}
            {holdCount > 0 && (
              <Box className={cx(classes.countBadge, classes.holdCount)}>
                <Typography variant="BD1">{holdCount}</Typography>
              </Box>
            )}
            {incompleteCount > 0 && (
              <Box className={cx(classes.countBadge, classes.incompleteCount)}>
                <Typography variant="BD1">{incompleteCount}</Typography>
              </Box>
            )}
          </Box>
        )}
        {menuData.length > 0 && (
          <Box>
            <MiMenu
              data={menuData}
              condensed
              showCloseDropdown
              boldText
              onMenuOpen={onMenuOpen}
              closeContextMenu={onMenuClose}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  headerContainer: {
    display: "flex",
    flex: 1,
    gap: theme.spacing(1),
    overflow: "hidden",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1),
    borderBottom: `4px solid ${theme.palette.common.black}`,
    borderTopRightRadius: 4,
    borderTopLeftRadius: 4,
  },
  userDetails: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: theme.spacing(1),
    overflow: "hidden",
  },
  rightContentContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    flexShrink: 0,
  },
  todayBadge: {
    padding: theme.spacing(0, 0.5),
    border: `1px solid ${theme.palette.info.main}`,
    backgroundColor: theme.palette.info.light,
    borderRadius: 2,
  },
  avatar: {
    width: 16,
    height: 16,
    fontSize: theme.fontSize.xxsmall,
  },
  titleText: {
    color: theme.palette.bluegrey.dark,
    textWrap: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  badgesContainer: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  countBadge: {
    padding: theme.spacing(0, 0.5),
    borderRadius: 2,
    display: "flex",
    minWidth: 16,
    justifyContent: "space-around",
    alignItems: "center",
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.grey.darkAccent}`,
    backgroundColor: theme.palette.grey.mediumLight,
    fontSize: theme.fontSize.xsmall,
  },
  holdCount: {
    color: theme.palette.hold.main,
    borderColor: theme.palette.hold.main,
    backgroundColor: theme.palette.hold.light,
  },
  incompleteCount: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.light,
  },
}));
